import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { PageContent, Spinner, CisContainer, Table } from 'components';
import PropTypes from 'prop-types';
import { globalPropTypes } from 'helpers';
import TableHeaderCell from 'components/Table/TableHeaderCell';
import CisTable from 'components/Table/CisTable';
import CisPagination from 'components/Table/CisPagination';

const COLUMN_COUNT = 3;

const Competencies = ({ loading, list, loadList, setPage, setPerPage, setSort, setFilters }) => {
  const { filters, items, page, pages, perPage, sort, totalItems } = list;
  const { t } = useTranslation();

  const typeOptions = [
    { value: 'competency', label: t('page.ma.cpdManagement.competencies.types.competency') },
    {
      value: 'peopleCompetency',
      label: t('page.ma.cpdManagement.competencies.types.peopleCompetency'),
    },
    {
      value: 'practiceCompetency',
      label: t('page.ma.cpdManagement.competencies.types.practiceCompetency'),
    },
  ];

  const getTypeLabel = (type) => typeOptions.find((option) => option.value === type)?.label || type;

  useEffect(() => {
    setPage(1);
    loadList();
  }, [perPage, sort]);

  const getSortDirection = (key) => {
    if (!sort) return null;
    const [sortKey, sortDir] = sort.split('.');
    return sortKey === key ? sortDir : null;
  };

  const handleColumnSort = (key, direction) => {
    setSort(direction ? `${key}.${direction}` : null);
  };

  const handleColumnFilterChange = (key, value) => {
    setFilters({ name: key, value });
    setPage(1);
    loadList();
  };

  return (
    <PageContent>
      <CisContainer>
        <CisTable>
          <CisTable.Head>
            <CisTable.Row>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="input"
                  filterValue={filters.id || ''}
                  onFilter={(val) => handleColumnFilterChange('id', val)}
                  filterPlaceholder={t('page.ma.cpdManagement.competencies.idPlaceholder')}
                  label={t('page.ma.cpdManagement.competencies.id')}
                  sort
                  sortDirection={getSortDirection('id')}
                  onSort={(dir) => handleColumnSort('id', dir)}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="select"
                  filterValue={filters.type || ''}
                  onFilter={(val) => handleColumnFilterChange('type', val)}
                  filterOptions={typeOptions}
                  filterPlaceholder={t('page.ma.cpdManagement.competencies.typePlaceholder')}
                  label={t('page.ma.cpdManagement.competencies.type')}
                  sort
                  sortDirection={getSortDirection('type')}
                  onSort={(dir) => handleColumnSort('type', dir)}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="input"
                  filterValue={filters.name || ''}
                  onFilter={(val) => handleColumnFilterChange('name', val)}
                  filterPlaceholder={t('page.ma.cpdManagement.competencies.namePlaceholder')}
                  label={t('page.ma.cpdManagement.competencies.name')}
                  sort
                  sortDirection={getSortDirection('name')}
                  onSort={(dir) => handleColumnSort('name', dir)}
                />
              </CisTable.StickyHeading>
            </CisTable.Row>
          </CisTable.Head>
          <CisTable.Body>
            {loading ? (
              <CisTable.Row>
                <CisTable.Cell colSpan={COLUMN_COUNT}>
                  <Spinner />
                </CisTable.Cell>
              </CisTable.Row>
            ) : null}
            {!loading && !items.length ? (
              <CisTable.Row>
                <CisTable.Cell colSpan={COLUMN_COUNT}>{t('global.noResultsFound')}</CisTable.Cell>
              </CisTable.Row>
            ) : null}
            {!loading && items.length
              ? items.map((item) => (
                  // Ids repeat across the types (there is a practice, a people and
                  // a perspective competency 1), so the type is part of the key.
                  <CisTable.Row key={`${item.type}-${item.id}`}>
                    <CisTable.Cell>
                      <Table.Label>{t('page.ma.cpdManagement.competencies.id')}:</Table.Label>
                      {item.id}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.ma.cpdManagement.competencies.type')}:</Table.Label>
                      {getTypeLabel(item.type)}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.ma.cpdManagement.competencies.name')}:</Table.Label>
                      {item.name}
                    </CisTable.Cell>
                  </CisTable.Row>
                ))
              : null}
          </CisTable.Body>
        </CisTable>
      </CisContainer>
      <CisPagination
        styled
        currentPage={page}
        totalPages={pages}
        totalItems={totalItems}
        perPage={perPage}
        onPageChange={(payload) => {
          setPage(payload);
          loadList();
        }}
        onPerPageChange={({ value }) => setPerPage(value)}
      />
    </PageContent>
  );
};

Competencies.propTypes = {
  loading: PropTypes.bool,
  list: globalPropTypes.ListPropsTypes.isRequired,
  loadList: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired,
  setPerPage: PropTypes.func.isRequired,
  setSort: PropTypes.func.isRequired,
  setFilters: PropTypes.func.isRequired,
};

Competencies.defaultProps = {
  loading: false,
};

export default Competencies;
